import React from 'react';
import { Box, Paper, Typography, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface OrderFormLayoutProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
  loadingLabel: string;
  cancelPath?: string;
  loading?: boolean;
}

const OrderFormLayout: React.FC<OrderFormLayoutProps> = ({
  title,
  icon,
  children,
  onSubmit,
  submitLabel,
  loadingLabel,
  cancelPath = '/customers/orders',
  loading = false,
}) => {
  const navigate = useNavigate();

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    // Prevent Enter key from submitting the form - only button clicks should submit
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <Paper sx={{ 
      p: { xs: 1.5, sm: 2, md: 2 }, 
      mx: { xs: 1, sm: 2, md: 'auto' }, 
      mt: 0,
      maxWidth: { xs: '100%', md: 1000, lg: 1100 },
      width: '100%',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ mr: 1, color: 'primary.main' }}>{icon}</Box>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
        {children}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button type="submit" variant="contained" size="medium" disabled={loading}>
            {loading ? loadingLabel : submitLabel}
          </Button>
          <Button onClick={() => navigate(cancelPath)} variant="outlined" size="medium" disabled={loading}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default OrderFormLayout;
